import type { DateRangeFns, Sort } from '@cobliteam/common-types';
import {
  filterGetParamDates,
  filterGetParamFilters,
  filterSetParamDates,
  filterSetParamFilters,
  type IFiltersDef,
  type SortingState,
  type TFilterKeys,
} from '@cobliteam/ui-business/components/DataTable';
import { DEFAULT_MEDIA_REQUEST_DATA, RequestTableType } from '@cobliteam/ui-business/utils/mediaUtils';
import { snakeCase } from 'change-case';
import type { StateCreator } from 'zustand';
import type { MediaRequestData } from '../../../types';

export interface MediaRequestSliceState {
  mediaRequestDates: DateRangeFns | undefined;
  mediaRequestFilters: IFiltersDef[];
  mediaRequestSort: Sort | undefined;
  mediaRequestTab: RequestTableType;
  requestMediaData: MediaRequestData;
  setMediaRequestDates: (dates: DateRangeFns) => void;
  setMediaRequestFilters: (filter: IFiltersDef) => void;
  setMediaRequestSort: (sorts: SortingState) => void;
  getRequestFilterValueByKey: <T>(key: TFilterKeys) => T | undefined;
  setMediaRequestTab: (tab: RequestTableType) => void;
  setRequestMediaData: (data: MediaRequestData) => void;
  clearMediaRequestFilters: () => void;
}

type StateCreatormediaRequestTable = StateCreator<MediaRequestSliceState, [['zustand/devtools', never]], [], MediaRequestSliceState>;

const mediaRequestSlice: StateCreatormediaRequestTable = (set, get) => ({
  mediaRequestDates: filterGetParamDates(),
  mediaRequestFilters: filterGetParamFilters(),
  mediaRequestSort: undefined,
  mediaRequestTab: RequestTableType.REQUEST_MEDIA,
  requestMediaData: DEFAULT_MEDIA_REQUEST_DATA,
  setMediaRequestDates: (dates: DateRangeFns) =>
    set(
      (): Partial<MediaRequestSliceState> => {
        filterSetParamDates(dates);
        return { mediaRequestDates: dates };
      },
      false,
      'setDates'
    ),
  setMediaRequestFilters: (filter: IFiltersDef) =>
    set(
      (state): Partial<MediaRequestSliceState> => {
        const filterDef = state.mediaRequestFilters.find((f: IFiltersDef) => f.key === filter.key) as IFiltersDef;
        const newFilter = {
          ...filterDef,
          ...filter,
        };
        // filterSetParamFilters(newFilter);

        // create a copy of the filters array
        const updatedFilters = [...state.mediaRequestFilters];

        // find the filter with the matching key and update its value
        const filterIndex = updatedFilters.findIndex((f) => f.key === filter.key);

        if (filterIndex !== -1) {
          updatedFilters[filterIndex].value = filter.value;
        }

        return { mediaRequestFilters: updatedFilters };
      },
      false,
      'setFilters'
    ),
  setMediaRequestSort: (sorts: SortingState) =>
    set(
      (): Partial<MediaRequestSliceState> => {
        const newSort = sorts.length
          ? {
              column: snakeCase(sorts[0].id),
              order: sorts[0].desc ? 'desc' : 'asc',
            }
          : undefined;

        return {
          mediaRequestSort: newSort as Sort,
        };
      },
      false,
      'setSort'
    ),
  getRequestFilterValueByKey: (key: TFilterKeys) => get().mediaRequestFilters.find((f: IFiltersDef) => f.key === key)?.value,
  setMediaRequestTab: (tab: RequestTableType) =>
    set(() => ({
      mediaRequestTab: tab,
    })),
  setRequestMediaData: (data: MediaRequestData) =>
    set(() => ({
      requestMediaData: data,
    })),
  clearMediaRequestFilters: () =>
    set(
      (state): Partial<MediaRequestSliceState> => {
        const filtersToClearKeys = [
          'MultiSelectVehicleGroups',
          'MultiSelectVehicles',
          'MultiSelectDrivers',
          'MultiSelectUsers',
          'MultiSelectMediaRequestType',
          'MultiSelectMediaRequestStatus',
        ];

        const updatedFilters = [...state.mediaRequestFilters];

        filtersToClearKeys.forEach((key) => {
          const filterIndex = updatedFilters.findIndex((f) => f.key === key);

          if (filterIndex !== -1 && updatedFilters[filterIndex].value.length > 0) {
            updatedFilters[filterIndex].value = [];

            filterSetParamFilters(updatedFilters[filterIndex]);
          }
        });

        return { mediaRequestFilters: updatedFilters };
      },
      false,
      'clearSelectedFilters'
    ),
});

export default mediaRequestSlice;
